import { useEffect, useMemo, useState } from "react";
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  Group,
  Button,
  Badge,
  ActionIcon,
  Stack,
  Pagination,
  Skeleton,
  Select,
  Modal,
} from "@mantine/core";
import { IconCheck, IconRefresh } from "@tabler/icons-react";
import "./styles.css";
import { Endpoints } from "../../../../lib/endpoints";
import { PaginatedResponse } from "../../../../lib/types";
import { HttpCodes } from "../../../../lib/data";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../../hooks/usePerformRequest";
import PermissionGuard from "../../../../components/PermissionGuard";
import { AdminPermissions } from "../../../../lib/data";

interface DeletionRequestProps {
  id: string;
  userId: string;
  userEmail: string;
  userName?: string;
  status: string;
  createdAt: string;
}

const Deletions = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState("approved"); // Looking for requests verified by user
  const [actionProcessing, setActionProcessing] = useState(false);

  useEffect(() => {
    document.title = "User Deletions | Virel Admin";
  }, []);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmRequest, setConfirmRequest] =
    useState<DeletionRequestProps | null>(null);

  const endpoint = useMemo(
    () =>
      Endpoints.GetDeletionRequests({
        page: currentPage,
        limit: pageSize,
        status: statusFilter,
      }),
    [currentPage, pageSize, statusFilter],
  );

  const { data, isLoading, reloadData } = usePerformRequest<
    PaginatedResponse<DeletionRequestProps[]>
  >({
    method: endpoint.method,
    route: endpoint.route,
    showError: true,
  });

  const requests = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / pageSize));
  const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd =
    totalRecords === 0 ? 0 : Math.min(currentPage * pageSize, totalRecords);

  const formatDate = (value?: string | Date) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value.toString();
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <Badge color="yellow" variant="light">
            Verified (Pending Admin Sync)
          </Badge>
        );
      case "pending":
        return (
          <Badge color="gray" variant="light">
            Unverified by User
          </Badge>
        );
      default:
        return (
          <Badge color="gray" variant="light">
            {status}
          </Badge>
        );
    }
  };

  const handleApproveDeletion = async () => {
    if (!confirmRequest) return;

    setActionProcessing(true);
    const deleteEndpoint = Endpoints.ApproveDeletionRequest(confirmRequest.id);

    const result = await PerformRequest({
      method: deleteEndpoint.method,
      route: deleteEndpoint.route,
    });

    setActionProcessing(false);

    if (
      result.status === HttpCodes.Success ||
      result.status === HttpCodes.Created
    ) {
      setConfirmOpen(false);
      setConfirmRequest(null);
      await reloadData();
    }
  };

  const rows = requests.map((req) => (
    <Table.Tr key={req.id}>
      <Table.Td>
        <Text fw={500} size="sm">
          {req.userName || req.userEmail}
        </Text>
        {req.userName && (
          <Text size="xs" c="dimmed">
            {req.userEmail}
          </Text>
        )}
      </Table.Td>
      <Table.Td>{formatDate(req.createdAt)}</Table.Td>
      <Table.Td>{getStatusBadge(req.status)}</Table.Td>
      <Table.Td>
        {req.status === "approved" && (
          <Button
            size="xs"
            color="red"
            variant="light"
            leftSection={<IconCheck size={14} />}
            onClick={() => {
              setConfirmRequest(req);
              setConfirmOpen(true);
            }}
          >
            Execute Deletion
          </Button>
        )}
      </Table.Td>
    </Table.Tr>
  ));

  const skeletonRows = Array.from({ length: Math.min(pageSize, 5) }).map(
    (_, idx) => (
      <Table.Tr key={`skeleton-${idx}`}>
        <Table.Td>
          <Skeleton height={20} width="60%" />
          <Skeleton height={12} width="40%" mt={4} />
        </Table.Td>
        <Table.Td>
          <Skeleton height={20} width="80%" />
        </Table.Td>
        <Table.Td>
          <Skeleton height={24} width={100} />
        </Table.Td>
        <Table.Td>
          <Skeleton height={30} width={120} />
        </Table.Td>
      </Table.Tr>
    ),
  );

  return (
    <PermissionGuard
      permission={AdminPermissions.MANAGE_USERS}
      fallback={
        <Container>
          <Text>Unauthorized</Text>
        </Container>
      }
    >
      <div className="users-page">
        <Container size="xl">
          <Stack gap="lg">
            <div>
              <Title order={1} className="page-title">
                Account Deletion Requests
              </Title>
              <Text size="sm" c="dimmed" mt={4}>
                Manage users who have requested and verified their account
                deletion via OTP.
              </Text>
            </div>

            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group gap="md" mb="md" justify="space-between">
                <Group gap="md">
                  <Select
                    label="Status Filter"
                    data={[
                      {
                        value: "approved",
                        label: "Verified (Ready for Deletion)",
                      },
                      { value: "pending", label: "Pending OTP Verification" },
                    ]}
                    value={statusFilter}
                    onChange={(v) => {
                      if (v) {
                        setStatusFilter(v);
                        setCurrentPage(1);
                      }
                    }}
                    size="sm"
                    style={{ width: 250 }}
                  />
                </Group>

                <Group gap="xs" align="center" mt={24}>
                  <Text size="sm" c="dimmed">
                    List Per Page
                  </Text>
                  <Select
                    data={["5", "10", "20", "50"]}
                    value={String(pageSize)}
                    onChange={(value) => {
                      setPageSize(Number(value) || 10);
                      setCurrentPage(1);
                    }}
                    size="sm"
                    w={100}
                  />
                </Group>
              </Group>

              <div className="table-container">
                <Table
                  striped
                  highlightOnHover
                  verticalSpacing="sm"
                  withColumnBorders
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>User Details</Table.Th>
                      <Table.Th>Requested On</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {isLoading ? (
                      skeletonRows
                    ) : rows.length > 0 ? (
                      rows
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={4} style={{ textAlign: "center" }}>
                          <Text c="dimmed" py="xl">
                            No deletion requests found
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </div>

              <Group justify="center" mt="md">
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => reloadData()}
                >
                  Refresh List
                </Button>
              </Group>

              {totalPages > 1 && (
                <Group justify="center" mt="md">
                  <Pagination
                    value={currentPage}
                    onChange={setCurrentPage}
                    total={totalPages}
                    size="sm"
                  />
                </Group>
              )}

              <Group
                justify="space-between"
                mt="md"
                pt="md"
                style={{ borderTop: "1px solid #e2e8f0" }}
              >
                <Text size="sm" c="dimmed">
                  Showing {rangeStart} to {rangeEnd} of {totalRecords} requests
                </Text>
              </Group>
            </Paper>
          </Stack>
        </Container>

        <Modal
          opened={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          title="Confirm Account Deletion"
          centered
        >
          <Text size="sm" mb="lg">
            Are you sure you want to permanently delete the account for{" "}
            <strong>{confirmRequest?.userEmail}</strong>? This action will
            remove all user data, proofs, and associated metadata. It cannot be
            undone.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleApproveDeletion}
              loading={actionProcessing}
            >
              Yes, Permanently Delete
            </Button>
          </Group>
        </Modal>
      </div>
    </PermissionGuard>
  );
};

export default Deletions;
